import { useDispatch } from "react-redux";
import CloseButtonModal from "../../../../ui/close-button-modal";
import { clearUpdateTypeModal } from "../../../../../features/gms-state-and-modal/modal-slice";
import ViewSection from "../../../../ui/view-section";
import { useProposalTypeData } from "../../../../../hooks/proposal/use-proposal-type-data";
import { useProposalData } from "../../../../../hooks/populate-data/use-proposal-data";
import RatesView from "../../common/rates-view";
import { formatNumberWithComma } from "../../../../../utils/format-numbers.utils";
import RichTextViewer from "../../../../../library/ckeditor/rich-text-viewer";
import { useEffect, useMemo, useRef } from "react";
import { useProposalComputation } from "../../../../../hooks/proposal/use-proposal-computation";
import { getFileIcon } from "../../../../ui/get-file-icon";
import { useFilesActions } from "../../../../../hooks/actions/use-files-actions";

export default function ComputationDetails() {

    const dispatch = useDispatch();
    const {
        proposal,
        isLoadingProposal,
        isErrorProposal,
        proposalErrorMessage,
        repopulateAll,
        repopulateRates,
        refetchProposal,
    } = useProposalData();
    const { 
        groupName, 
        natureOfBusinessItem, 
        isRatesIsAgeType
    } = useProposalTypeData();
    const { 
        rates1865, 
        rates6670, 
        rates7175, 
        rates7680, 
        selectShowRates,
        annualPremiumAmount,
        evidenceNotes,
        documents: documentFiles,
        actNotePDF,
        actNoteForCFE
    } = useProposalComputation();
    const { 
        handleDownloadMasterFile, 
        handleDownloadSupportingDocumentsCFE, 
        handleDownloadSupportingDocumentsAct: handleDownloadFileActuarial,
    } = useFilesActions();

    const firstTheadTitle = isRatesIsAgeType
        ? "Attained Age"
        : "Term of Loan";

    const tableTitle = isRatesIsAgeType
        ? "Enrolling Age 18-65"
        : "Borrowers Age 18-65";

    const handleCloseModal = () => {
        dispatch(clearUpdateTypeModal());
    };

    // CHANGED: Removed the early return checks for 66-70, 71-75, and 76-80.
    // Empty rate groups should not cause the entire modal to disappear.

    // CHANGED: Safely get the 66-70 data.
    const ages6670 = rates6670?.map((group) => group.title) ?? [];
    const terms6670 = rates6670?.[0]?.data ?? [];
    const theadNames6670 = terms6670.map((item) => item.acronym);
    const theadTitles6670 = isRatesIsAgeType
        ? theadNames6670
        : ages6670;
    const tableTitle6670 = isRatesIsAgeType
        ? "Enrolling Age 66-70"
        : "Borrowers Age 66-70";

    // CHANGED: Safely get the 71-75 data.
    const ages7175 = rates7175?.map((group) => group.title) ?? [];
    const terms7175 = rates7175?.[0]?.data ?? [];
    const theadNames7175 = terms7175.map((item) => item.acronym);
    const theadTitles7175 = isRatesIsAgeType
        ? theadNames7175
        : ages7175;
    const tableTitle7175 = isRatesIsAgeType
        ? "Enrolling Age 71-75"
        : "Borrowers Age 71-75";

    // CHANGED: Fixed variable name from tableTitle77680 to tableTitle7680.
    // Also safely handle an empty 76-80 rate group.
    const ages7680 = rates7680?.map((group) => group.title) ?? [];
    const terms7680 = rates7680?.[0]?.data ?? [];
    const theadNames7680 = terms7680.map((item) => item.acronym);
    const theadTitles7680 = isRatesIsAgeType
        ? theadNames7680
        : ages7680;
    const tableTitle7680 = isRatesIsAgeType
        ? "Enrolling Age 76-80"
        : "Borrowers Age 76-80";


    const theadNames1865: string[] = rates1865?.[0]?.data?.map((item) => item.acronym) ?? [];

    const sortedFiles = [...documentFiles].sort((a, b) => {
        if (a.type === b.type) return 0;
        return a.type === "new" ? -1 : 1;
    });
    
    const populatedProposalRef = useRef<string | null>(null);
    const proposalSignature = useMemo(
        () => (proposal ? JSON.stringify(proposal) : null),
        [proposal],
    );
    useEffect(() => {
        if (!proposal?.application_id || !proposalSignature) {
            return;
        }

        if (populatedProposalRef.current === proposalSignature) {
            return;
        }

        populatedProposalRef.current = proposalSignature;

        repopulateAll();
        repopulateRates();
    }, [
        proposal?.application_id,
        repopulateAll,
        repopulateRates,
        proposalSignature,
    ]);

    return (
        <div className="fixed inset-0 z-50 flex items-start justify-center bg-black/40 p-4">
            <div className="my-auto flex h-[96dvh] w-full flex-col overflow-hidden rounded bg-[#f5f9fd] shadow sm:h-[90dvh] sm:max-w-[95vw] lg:max-w-[700px] xl:max-w-[1000px] 2xl:max-w-[1100px]">
                {/* Header */}
                <div className="flex shrink-0 items-start justify-between border-b border-gray-200 bg-white px-4 py-3 sm:px-5">
                    <div className="min-w-0 flex-1">
                        <div className="flex min-w-0 items-center gap-2">
                            <div className="h-2 w-2 shrink-0 rounded-full bg-[#186cb2]" />
                            <p className="truncate text-sm font-semibold text-gray-800">
                                {groupName ?? "-"}
                            </p>
                        </div>
                        <p className="mt-1 truncate pl-4 text-[11px] text-gray-500">
                            {natureOfBusinessItem?.name ?? "No nature of business specified"}
                        </p>
                    </div>
                    <div className="ml-3 flex shrink-0 items-center">
                        <CloseButtonModal handleClose={handleCloseModal} />
                    </div>
                </div>
                {/* Content */}
                <div className="min-h-0 flex-1 overflow-y-auto">
                    {isLoadingProposal ? (
                        <div className="flex min-h-full items-center justify-center p-5">
                            <div className="flex w-full max-w-sm flex-col items-center justify-center rounded-xl border border-gray-200 bg-white p-6 shadow-sm">
                                <div className="flex h-10 w-10 items-center justify-center rounded-full bg-[#eef6fc]">
                                    <div className="h-5 w-5 animate-spin rounded-full border-2 border-[#186cb2]/20 border-t-[#186cb2]" />
                                </div>
                                <p className="mt-3 text-xs font-semibold text-gray-800">
                                    Loading computation details
                                </p>
                                <p className="mt-1 text-center text-[11px] text-gray-500">
                                    Please wait while the proposal and
                                    computation data are loaded.
                                </p>
                            </div>
                        </div>
                    ) : isErrorProposal ? (
                        <div className="flex min-h-full items-center justify-center p-5">
                            <div className="flex w-full max-w-sm flex-col items-center justify-center rounded-xl border border-red-200 bg-white p-6 text-center shadow-sm">
                                <div className="flex h-10 w-10 items-center justify-center rounded-full bg-red-50 text-red-500">
                                    <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="1.8" >
                                        <circle cx="12" cy="12" r="9" />
                                        <path d="M12 8v5M12 16h.01" strokeLinecap="round" />
                                    </svg>
                                </div>
                                <p className="mt-3 text-xs font-semibold text-gray-800">
                                    Unable to load computation details
                                </p>
                                <p className="mt-1 text-[11px] leading-5 text-gray-500">
                                    {proposalErrorMessage ||
                                        "Something went wrong while loading the proposal."}
                                </p>
                                <button
                                    type="button"
                                    onClick={refetchProposal}
                                    className="mt-4 rounded-lg bg-[#186cb2] px-4 py-2 text-xs font-semibold text-white transition hover:bg-[#125b99]"
                                >
                                    Try Again
                                </button>
                            </div>
                        </div>
                    ) : (
                        <div className="space-y-3 p-3 sm:p-4 lg:p-5">
                            {/* Rates */}
                            <ViewSection
                                title="Plan and Riders"
                                description="Available plans, riders, and corresponding rates."
                            >
                                {/* Plans / Riders */}
                                <div className="mb-3 grid grid-cols-1 gap-2 sm:grid-cols-2 xl:grid-cols-3">
                                    {rates1865?.[0]?.data?.map(
                                        (rate, index) => (
                                            <div key={index} className="flex min-w-0 items-start gap-1.5 rounded-lg border border-gray-200 bg-gray-50 px-2.5 py-2 text-xs" >
                                                <p className="min-w-0 truncate text-gray-700">
                                                    <span className="font-semibold text-[#186cb2]">
                                                        {rate.acronym}{" "}
                                                        {" - "}
                                                    </span>
                                                    {rate.name}
                                                </p>
                                            </div>
                                        )
                                    )}
                                </div>
                                {/* Rate Table */}
                                {/* 18-65 */}
                                <div className="space-y-3">
                                    <RatesView
                                        isShow={true}
                                        title={tableTitle}
                                        theadTitles={theadNames1865}
                                        firstTheadTitle={firstTheadTitle}
                                        typeOfRates={true}
                                        rates={[]}
                                        ratesPerAge={rates1865 ?? []}
                                    />
                                    {/* 66-70 */}
                                    <RatesView
                                        isShow={selectShowRates?.isShow6670 ?? false}
                                        title={tableTitle6670}
                                        theadTitles={theadTitles6670}
                                        firstTheadTitle={firstTheadTitle}
                                        typeOfRates={isRatesIsAgeType}
                                        rates={terms6670}
                                        ratesPerAge={rates6670 ?? []}
                                    />
                                    {/* 71-75 */}
                                    <RatesView
                                        isShow={selectShowRates?.isShow7175 ?? false}
                                        title={tableTitle7175}
                                        theadTitles={theadTitles7175}
                                        firstTheadTitle={firstTheadTitle}
                                        typeOfRates={isRatesIsAgeType}
                                        rates={terms7175}
                                        ratesPerAge={rates7175 ?? []}
                                    />
                                    {/* 76-80 */}
                                    <RatesView
                                        isShow={selectShowRates?.isShow7680 ?? false}
                                        title={tableTitle7680}
                                        theadTitles={theadTitles7680}
                                        firstTheadTitle={firstTheadTitle}
                                        typeOfRates={isRatesIsAgeType}
                                        rates={terms7680}
                                        ratesPerAge={rates7680 ?? []}
                                    />
                                </div>
                            </ViewSection>
                            <ViewSection
                                title="Total Annual Premium"
                                description="Total premium calculated based on the selected plan and riders."
                            >
                                <div className="flex flex-col items-center justify-center">
                                    <span className="text-[10px] font-medium uppercase tracking-wide text-gray-400">
                                        Annual Premium
                                    </span>
                                    <div className="mt-1.5 flex min-w-0 items-baseline justify-center gap-1.5">
                                        <span className="text-lg font-semibold text-gray-500">₱</span>
                                        <span className="text-2xl font-bold text-[#186cb2]">
                                                {formatNumberWithComma(annualPremiumAmount ?? 0)}
                                        </span>
                                    </div>
                                    <p className="mt-1.5 text-[10px] text-gray-400">
                                        This amount will be reflected in the proposal.
                                    </p>
                                </div>
                            </ViewSection>
                            <ViewSection
                                title="Evidence of Insurability"
                                description="Medical requirements and underwriting notes for this proposal."
                            >
                                <div className="min-w-0">
                                    <RichTextViewer isView={true} value={evidenceNotes} />
                                </div>
                            </ViewSection>
                            <ViewSection
                                title="Documents"
                                description="Supporting documents required for this proposal."
                            >
                                <div className="">
                                    <div className="overflow-hidden">
                                        <div className="divide-y divide-gray-100">
                                            {sortedFiles?.length > 0 ? (
                                                sortedFiles.map((file, index) => (
                                                    <div
                                                        key={`${file.id}-${index}`}
                                                        className="grid grid-cols-12 items-center gap-2 px-2 py-1.5 transition hover:bg-gray-50"
                                                    >
                                                        {/* FILE */}
                                                        <div className="col-span-9 flex min-w-0 items-center gap-2">
                                                            <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded">
                                                                {getFileIcon(file.fileName)}
                                                            </div>
                                                            <p className="min-w-0 truncate text-xs font-medium text-gray-700">
                                                                {file.fileName}
                                                            </p>
                                                        </div>
                                                        {/* ACTIONS */}
                                                        <div className="col-span-3 flex items-center justify-end gap-1">
                                                            {/* VIEW */}
                                                            {/* <button
                                                                type="button"
                                                                className="inline-flex h-7 items-center gap-1 rounded-md px-2 text-[10px] font-medium text-gray-500 transition hover:bg-blue-50 hover:text-[#186cb2]"
                                                                title="View document"
                                                            >
                                                                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" className="h-3.5 w-3.5" aria-hidden="true">
                                                                    <path strokeLinecap="round" strokeLinejoin="round" d="M2.5 12s3.5-6 9.5-6 9.5 6 9.5 6-3.5 6-9.5 6-9.5-6-9.5-6Z" />
                                                                    <circle cx="12" cy="12" r="2.5" />
                                                                </svg>
                                                                <span className="hidden sm:inline">View</span>
                                                            </button> */}
                                                            {/* DOWNLOAD */}
                                                            <button
                                                                type="button"
                                                                onClick={() => handleDownloadFileActuarial(file.filePath, file.fileName)}
                                                                className="inline-flex h-7 items-center gap-1 rounded-md px-2 text-[10px] font-medium text-gray-500 transition hover:bg-blue-50 hover:text-[#186cb2]"
                                                                title="Download document"
                                                            >
                                                                <svg viewBox="0 0 24 24" fill="none" className="h-3.5 w-3.5" aria-hidden="true">
                                                                    <path d="M12 3v12m0 0 4-4m-4 4-4-4M5 21h14" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
                                                                </svg>
                                                                <span className="hidden sm:inline">Download</span>
                                                            </button>
                                                        </div>
                                                    </div>
                                                ))
                                            ) : (
                                                <div className="flex flex-col items-center justify-center px-4 py-8 text-center">
                                                    <div className="flex h-9 w-9 items-center justify-center rounded-full bg-gray-100">
                                                        <svg viewBox="0 0 24 24" className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" strokeWidth="1.7" >
                                                            <path d="M7 3.5h7l4 4V20a.5.5 0 0 1-.5.5h-10A.5.5 0 0 1 7 20V3.5Z" strokeLinecap="round" strokeLinejoin="round" />
                                                            <path d="M14 3.5V8h4" strokeLinecap="round" strokeLinejoin="round" />
                                                            <path d="M9.5 12h5M9.5 15h5" strokeLinecap="round" />
                                                        </svg>
                                                    </div>
                                                    <p className="mt-2 text-xs font-medium text-gray-600">
                                                        No documents available
                                                    </p>
                                                    <p className="mt-0.5 text-[11px] text-gray-400">
                                                        No supporting documents have been uploaded.
                                                    </p>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </ViewSection>
                            <ViewSection
                                title="Notes for Proposal"
                                description="Notes that will appear in the proposal document for the client."
                            >
                                <div className="min-w-0">
                                    <RichTextViewer isView={true} value={actNotePDF} />
                                </div>
                            </ViewSection>
                            <ViewSection
                                title="Notes for CFE"
                                description="Internal notes or instructions for the CFE team."
                            >
                                <div className="min-w-0">
                                    <RichTextViewer isView={true} value={actNoteForCFE} />
                                </div>
                            </ViewSection>
                        </div>
                    )}
                </div>
                {/* Footer */}
                {/* <div className="shrink-0 border-t border-gray-200 bg-white px-4 py-2.5 sm:px-5">
                    <div className="flex justify-end">
                        <button
                            type="button"
                            onClick={handleCloseModal}
                            className="w-full rounded-lg border border-gray-300 px-4 py-2 text-xs font-medium text-gray-600 transition hover:bg-gray-50 sm:w-auto"
                        >
                            Close
                        </button>
                    </div>
                </div> */}
            </div>
            {/* <LoadingFile /> */}
            {/* <LoadingViewFile /> */}
        </div>
    );
}